import { proxyCustomElement, HTMLElement, createEvent, h } from '@stencil/core/internal/client';
import { J as JustifiAnalytics } from './Analytics.js';
import { C as ComponentErrorCodes, a as ComponentErrorSeverity } from './ComponentError.js';
import { m as makeGetBusiness, e as makePostProvisioning } from './payment-provisioning-actions.js';
import { B as BusinessService } from './business.service.js';
import { c as config } from './config.js';
import { A as Api } from './Api.js';
import { c as checkPkgVersion } from './check-pkg-version.js';
import { d as defineCustomElement$y } from './form-control-checkbox2.js';
import { d as defineCustomElement$x } from './form-control-date2.js';
import { d as defineCustomElement$w } from './form-control-error-text2.js';
import { d as defineCustomElement$v } from './form-control-file2.js';
import { d as defineCustomElement$u } from './form-control-help-text2.js';
import { d as defineCustomElement$t } from './form-control-monetary2.js';
import { d as defineCustomElement$s } from './form-control-number-masked2.js';
import { d as defineCustomElement$r } from './form-control-select2.js';
import { d as defineCustomElement$q } from './form-control-text2.js';
import { d as defineCustomElement$p } from './business-additional-questions-form-step.js';
import { d as defineCustomElement$o } from './business-additional-questions-form-step-core.js';
import { d as defineCustomElement$n } from './business-bank-account-form-step.js';
import { d as defineCustomElement$m } from './business-core-info-form-step.js';
import { d as defineCustomElement$l } from './business-core-info-form-step-core.js';
import { d as defineCustomElement$k } from './business-document-upload-form-step.js';
import { d as defineCustomElement$j } from './business-document-upload-input-group.js';
import { d as defineCustomElement$i } from './business-documents-on-file.js';
import { d as defineCustomElement$h } from './business-owners-form-step.js';
import { d as defineCustomElement$g } from './business-owners-form-step-core.js';
import { d as defineCustomElement$f } from './business-representative-form-inputs.js';
import { d as defineCustomElement$e } from './business-representative-form-step.js';
import { d as defineCustomElement$d } from './business-representative-form-step-core.js';
import { d as defineCustomElement$c } from './business-terms-conditions-form-step.js';
import { d as defineCustomElement$b } from './business-terms-conditions-text.js';
import { d as defineCustomElement$a } from './identity-address-form.js';
import { d as defineCustomElement$9 } from './legal-address-form-step.js';
import { d as defineCustomElement$8 } from './legal-address-form-step-core.js';
import { d as defineCustomElement$7 } from './owner-form.js';
import { d as defineCustomElement$6 } from './payment-provisioning-core.js';
import { d as defineCustomElement$5 } from './payment-provisioning-form-buttons.js';
import { d as defineCustomElement$4 } from './payment-provisioning-form-steps.js';
import { d as defineCustomElement$3 } from './owner-form-core2.js';
import { d as defineCustomElement$2 } from './owner-form-inputs2.js';

class ProvisionService {
    async postProvisioning(authToken, businessId, product) {
        const endpoint = `entities/provisioning`;
        const payload = {
            business_id: businessId,
            product_category: product,
        };
        return Api({ authToken, apiOrigin: config.proxyApiOrigin }).post(endpoint, payload);
    }
}

const PaymentProvisioning = /*@__PURE__*/ proxyCustomElement(class PaymentProvisioning extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.errorEvent = createEvent(this, "error-event", 7);
        this.postProvisioning = undefined;
        this.getBusiness = undefined;
        this.businessId = undefined;
        this.authToken = undefined;
        this.allowOptionalFields = false;
        this.formTitle = 'Business Information';
    }
    propChanged() {
        this.initializeApi();
    }
    componentWillLoad() {
        checkPkgVersion();
        this.analytics = new JustifiAnalytics(this);
        this.initializeApi();
    }
    disconnectedCallback() {
        this.analytics.cleanup();
    }
    initializeApi() {
        if (this.authToken && this.businessId) {
            this.getBusiness = makeGetBusiness({
                authToken: this.authToken,
                businessId: this.businessId,
                service: new BusinessService()
            });
            this.postProvisioning = makePostProvisioning({
                authToken: this.authToken,
                businessId: this.businessId,
                product: 'payment',
                service: new ProvisionService()
            });
        }
        else {
            this.errorEvent.emit({
                message: 'auth-token and business-id are required',
                errorCode: ComponentErrorCodes.MISSING_PROPS,
                severity: ComponentErrorSeverity.ERROR,
            });
        }
    }
    render() {
        return (h("justifi-payment-provisioning-core", { key: '82b3601d3349a92bed1aa93e2a847039d7493c04', businessId: this.businessId, authToken: this.authToken, allowOptionalFields: this.allowOptionalFields, formTitle: this.formTitle, getBusiness: this.getBusiness, postProvisioning: this.postProvisioning }));
    }
    static get watchers() { return {
        "authToken": ["propChanged"],
        "businessId": ["propChanged"]
    }; }
}, [0, "justifi-payment-provisioning", {
        "businessId": [1, "business-id"],
        "authToken": [1, "auth-token"],
        "allowOptionalFields": [4, "allow-optional-fields"],
        "formTitle": [1, "form-title"],
        "postProvisioning": [32],
        "getBusiness": [32]
    }, undefined, {
        "authToken": ["propChanged"],
        "businessId": ["propChanged"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["justifi-payment-provisioning", "form-control-checkbox", "form-control-date", "form-control-error-text", "form-control-file", "form-control-help-text", "form-control-monetary", "form-control-number-masked", "form-control-select", "form-control-text", "justifi-additional-questions-form-step", "justifi-additional-questions-form-step-core", "justifi-business-bank-account-form-step", "justifi-business-core-info-form-step", "justifi-business-core-info-form-step-core", "justifi-business-document-upload-form-step", "justifi-business-document-upload-input-group", "justifi-business-documents-on-file", "justifi-business-owners-form-step", "justifi-business-owners-form-step-core", "justifi-business-representative-form-inputs", "justifi-business-representative-form-step", "justifi-business-representative-form-step-core", "justifi-business-terms-conditions-form-step", "justifi-business-terms-conditions-text", "justifi-identity-address-form", "justifi-legal-address-form-step", "justifi-legal-address-form-step-core", "justifi-owner-form", "justifi-payment-provisioning-core", "justifi-payment-provisioning-form-buttons", "justifi-payment-provisioning-form-steps", "owner-form-core", "owner-form-inputs"];
    components.forEach(tagName => { switch (tagName) {
        case "justifi-payment-provisioning":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, PaymentProvisioning);
            }
            break;
        case "form-control-checkbox":
            if (!customElements.get(tagName)) {
                defineCustomElement$y();
            }
            break;
        case "form-control-date":
            if (!customElements.get(tagName)) {
                defineCustomElement$x();
            }
            break;
        case "form-control-error-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$w();
            }
            break;
        case "form-control-file":
            if (!customElements.get(tagName)) {
                defineCustomElement$v();
            }
            break;
        case "form-control-help-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$u();
            }
            break;
        case "form-control-monetary":
            if (!customElements.get(tagName)) {
                defineCustomElement$t();
            }
            break;
        case "form-control-number-masked":
            if (!customElements.get(tagName)) {
                defineCustomElement$s();
            }
            break;
        case "form-control-select":
            if (!customElements.get(tagName)) {
                defineCustomElement$r();
            }
            break;
        case "form-control-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$q();
            }
            break;
        case "justifi-additional-questions-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$p();
            }
            break;
        case "justifi-additional-questions-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$o();
            }
            break;
        case "justifi-business-bank-account-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$n();
            }
            break;
        case "justifi-business-core-info-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$m();
            }
            break;
        case "justifi-business-core-info-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$l();
            }
            break;
        case "justifi-business-document-upload-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$k();
            }
            break;
        case "justifi-business-document-upload-input-group":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "justifi-business-documents-on-file":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "justifi-business-owners-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "justifi-business-owners-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "justifi-business-representative-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "justifi-business-representative-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "justifi-business-representative-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "justifi-business-terms-conditions-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "justifi-business-terms-conditions-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "justifi-identity-address-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "justifi-legal-address-form-step":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "justifi-legal-address-form-step-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "justifi-owner-form":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "justifi-payment-provisioning-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "justifi-payment-provisioning-form-buttons":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "justifi-payment-provisioning-form-steps":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "owner-form-core":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "owner-form-inputs":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const JustifiPaymentProvisioning = PaymentProvisioning;
const defineCustomElement = defineCustomElement$1;

export { JustifiPaymentProvisioning, defineCustomElement };
